import { API_BASE } from "./config/api";
import React, { useEffect, useState } from "react";
import SessionActivityMonitor from "./components/SessionActivityMonitor";
import useNativeBackGuard from "./components/useNativeBackGuard";
import { Layout, Menu, Button, Drawer, Grid, Tooltip, Modal, Form, Input, Alert, Result, message } from "antd";
import {
  DashboardOutlined,
  LogoutOutlined,
  MenuOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  PieChartOutlined,
  FileTextOutlined,
  LockOutlined,
  SafetyOutlined,
  BellOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import { Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  clearClientSession,
  getClientToken,
  handleClientAuthFailure,
} from "./config/auth";
import {
  disableBiometricLogin,
  enableBiometricLogin,
  getBiometricCapability,
  hasBiometricLogin,
  isBiometricCancellation,
  isNativeBiometricRealm,
  readBiometricLogin,
} from "./config/biometricAuth";
import { initializeMyWealthPush, unregisterMyWealthPushFromServer } from "./config/pushNotifications";
import { hasPinCredential, readPinCredential } from "./config/clientQuickAuth";
import mfsetuLogo from "./assets/mfsetu-mywealth-logo.png";
import "./ClientLogin.css";

const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;

function MpinDigits({ value, onChange, disabled = false, ariaLabel = "MPIN", autoFocus = false, onComplete = null, groupId = "change-mpin" }) {
  const refs = React.useRef([]);
  const digits = String(value || "").padEnd(4, " ").slice(0, 4).split("");
  React.useEffect(() => {
    if (!disabled && autoFocus) { const t = window.setTimeout(() => refs.current[0]?.focus(), 80); return () => window.clearTimeout(t); }
  }, [autoFocus, disabled]);
  const commit = (next) => onChange(next.join("").replace(/ /g, ""));
  const setDigit = (index, raw) => {
    const digit = String(raw || "").replace(/\D/g, "").slice(-1); if (!digit) return;
    const next = String(value || "").padEnd(4, " ").slice(0, 4).split(""); next[index] = digit; commit(next);
    if (index < 3) refs.current[index + 1]?.focus(); else if (onComplete) window.setTimeout(onComplete, 0);
  };
  const keyDown = (index, e) => {
    if (e.key !== "Backspace") return; e.preventDefault();
    const next = String(value || "").padEnd(4, " ").slice(0, 4).split("");
    if (next[index] && next[index] !== " ") { next[index] = " "; commit(next); return; }
    if (index > 0) { next[index - 1] = " "; commit(next); refs.current[index - 1]?.focus(); } else refs.current[0]?.focus();
  };
  return <div className="mf-mpin-digits" aria-label={ariaLabel}>{digits.map((digit,index)=><input id={`${groupId}-${index}`} ref={el=>{refs.current[index]=el}} key={index} className="mf-mpin-digit" type="password" inputMode="numeric" maxLength={1} value={digit.trim()} disabled={disabled} aria-label={`${ariaLabel} digit ${index+1}`} onFocus={e=>e.target.select()} onChange={e=>setDigit(index,e.target.value)} onKeyDown={e=>keyDown(index,e)} />)}</div>;
}

function AppLayoutForClient() {
  const authToken = getClientToken();
  useNativeBackGuard("/client/dashboard");
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isDesktop = screens.lg;
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const clientName = localStorage.getItem("client_name") || "Client";
  const [dashboardPan, setDashboardPan] = useState("");
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  const [changePasswordError, setChangePasswordError] = useState("");
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [changePasswordForm] = Form.useForm();
  const [changeMpinOpen, setChangeMpinOpen] = useState(false);
  const [changeMpinLoading, setChangeMpinLoading] = useState(false);
  const [changeMpinError, setChangeMpinError] = useState("");
  const [changeMpinSuccess, setChangeMpinSuccess] = useState(false);
  const [currentMpin, setCurrentMpin] = useState("");
  const [newMpin, setNewMpin] = useState("");
  const [confirmMpin, setConfirmMpin] = useState("");
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [biometricBusy, setBiometricBusy] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!isNativeBiometricRealm("client")) return;
      const capability = await getBiometricCapability("client");
      const enabled = capability.available ? await hasBiometricLogin("client") : false;
      if (alive) {
        setBiometricAvailable(capability.available);
        setBiometricEnabled(enabled);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!authToken) return;
    initializeMyWealthPush().catch(() => {});
  }, [authToken]);

  useEffect(() => {
    // Hooks must always run in the same order. If there is no token,
    // the protected layout redirects below and no API request is made.
    if (!authToken) return;

    const token = authToken;

    fetch(`${API_BASE}/client_me.php`, {
      method: "GET",
      headers: {
        "X-KG-Client-Authorization": `Bearer ${token}`,
      },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (handleClientAuthFailure(res, data)) return null;
        if (!res.ok) throw new Error(data?.message || "Unable to verify client session.");
        return data;
      })
      .then((data) => {
        if (!data) return;
        if (data?.status === "ok" && data?.client?.dashboard_pan) {
          setDashboardPan(data.client.dashboard_pan);
        }
      })
      .catch(() => {
        // Testing display only. Never fall back to a browser-supplied PAN.
        setDashboardPan("");
      });
  }, [authToken]);

  // Do not render protected client content without a session token.
  // Keep this redirect after all Hooks so React Hook ordering stays valid.
  if (!authToken) return <Navigate to="/client/login" replace />;

  const openChangePassword = () => {
    setDrawerOpen(false);
    setChangePasswordError("");
    setChangePasswordSuccess(false);
    changePasswordForm.resetFields();
    setChangePasswordOpen(true);
  };

  const closeChangePassword = () => {
    if (changePasswordLoading) return;
    setChangePasswordOpen(false);
    setChangePasswordError("");
    setChangePasswordSuccess(false);
    changePasswordForm.resetFields();
  };

  const submitChangePassword = async (values) => {
    setChangePasswordLoading(true);
    setChangePasswordError("");

    try {
      const token = getClientToken();

      if (!token) {
        clearClientSession();
        window.location.replace("/client/login");
        return;
      }

      const response = await fetch(`${API_BASE}/client_change_password.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-KG-Client-Authorization": `Bearer ${token}`,
        },
        body: JSON.stringify({
          current_password: values.current_password,
          new_password: values.new_password,
          confirm_password: values.confirm_password,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (handleClientAuthFailure(response, data)) {
        return;
      }

      if (!response.ok || data.status !== "ok") {
        setChangePasswordError(
          data.message || "Unable to change password at this time."
        );
        return;
      }

      setChangePasswordError("");
      changePasswordForm.resetFields();
      setChangePasswordSuccess(true);
    } catch (error) {
      setChangePasswordError(
        error.message || "Unable to connect to server."
      );
    } finally {
      setChangePasswordLoading(false);
    }
  };

  const openChangeMpin = async () => {
    setDrawerOpen(false);
    setChangeMpinError(""); setChangeMpinSuccess(false);
    setCurrentMpin(""); setNewMpin(""); setConfirmMpin("");

    // Always open the screen, including on localhost/browser.  MPIN itself is
    // device-bound, so a browser without the native secure credential can
    // view the screen and gets a clear explanation instead of a dead click.
    const configured = await hasPinCredential();
    if (!configured) {
      setChangeMpinError(
        "MPIN is not configured on this device. MPIN is device-bound; open MFSetu MyWealth on the mobile device where your MPIN is configured to change it. If no MPIN exists yet, use Set Up 4 Digit MPIN from the login screen."
      );
    }
    setChangeMpinOpen(true);
  };

  const closeChangeMpin = () => {
    if (changeMpinLoading) return;
    setChangeMpinOpen(false); setChangeMpinError(""); setChangeMpinSuccess(false);
    setCurrentMpin(""); setNewMpin(""); setConfirmMpin("");
  };

  const submitChangeMpin = async () => {
    setChangeMpinError("");
    if (!/^\d{4}$/.test(currentMpin)) { setChangeMpinError("Please enter your current 4 digit MPIN."); return; }
    if (!/^\d{4}$/.test(newMpin)) { setChangeMpinError("Please enter a complete new 4 digit MPIN."); return; }
    if (newMpin !== confirmMpin) { setChangeMpinError("New MPIN and Confirm MPIN do not match."); return; }
    if (newMpin === currentMpin) { setChangeMpinError("New MPIN cannot be the same as your current MPIN."); return; }
    setChangeMpinLoading(true);
    try {
      const saved = await readPinCredential();
      if (!saved?.deviceCredential) throw new Error("MPIN is not configured on this device. Please set it again from the login screen.");
      const token = getClientToken();
      if (!token) throw new Error("Your session has expired. Please sign in again.");
      const response = await fetch(`${API_BASE}/client_pin_change.php`, {
        method: "POST",
        headers: {"Content-Type":"application/json","X-KG-Client-Authorization":`Bearer ${token}`},
        body: JSON.stringify({device_credential:saved.deviceCredential,current_pin:currentMpin,new_pin:newMpin,confirm_pin:confirmMpin}),
      });
      const data = await response.json().catch(() => ({}));
      if (handleClientAuthFailure(response, data)) return;
      if (!response.ok || data.status !== "ok") throw new Error(data.message || "Unable to change MPIN.");
      setCurrentMpin(""); setNewMpin(""); setConfirmMpin(""); setChangeMpinSuccess(true);
    } catch (error) { setChangeMpinError(error.message || "Unable to change MPIN. Please try again."); }
    finally { setChangeMpinLoading(false); }
  };

  const toggleBiometric = async () => {
    if (biometricBusy || !biometricAvailable) return;
    setDrawerOpen(false);
    setBiometricBusy(true);
    try {
      if (biometricEnabled) {
        // Reading the stored device credential requires biometric authentication.
        // Revoke it on the server first; only then remove the local secure copy.
        const saved = await readBiometricLogin("client");
        if (!saved?.deviceCredential) {
          await disableBiometricLogin("client");
          setBiometricEnabled(false);
          message.success("Biometric login disabled on this device.");
          return;
        }

        await fetch(`${API_BASE}/client_biometric_revoke.php`, {
          method: "POST",
          headers: {
            "X-KG-Biometric-Authorization": `Bearer ${saved.deviceCredential}`,
          },
        }).then(async (response) => {
          const data = await response.json().catch(() => ({}));
          if (!response.ok || data?.status !== "ok") {
            throw new Error(data?.message || "Unable to revoke biometric credential.");
          }
        });

        await disableBiometricLogin("client");
        setBiometricEnabled(false);
        message.success("Biometric login disabled on this device.");
      } else {
        const token = getClientToken();
        if (!token) throw new Error("Session expired.");

        const enrollRes = await fetch(`${API_BASE}/client_biometric_enroll.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-KG-Client-Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ device_label: "MFSetu - MyWealth" }),
        });
        const enrollData = await enrollRes.json().catch(() => ({}));
        const deviceCredential = enrollData?.device_credential;
        if (!enrollRes.ok || enrollData?.status !== "ok" || !deviceCredential) {
          throw new Error(enrollData?.message || "Biometric enrollment failed.");
        }

        await enableBiometricLogin("client", {
          deviceCredential,
          userId: localStorage.getItem("client_user_id") || "",
          displayName: localStorage.getItem("client_name") || "",
        });
        setBiometricEnabled(true);
        message.success("Biometric login enabled on this device.");
      }
    } catch (error) {
      if (!isBiometricCancellation(error)) message.error("Unable to change biometric login setting.");
    } finally {
      setBiometricBusy(false);
    }
  };

  const openNotifications = async () => {
    setDrawerOpen(false);
    setNotificationsOpen(true);
    setNotificationsLoading(true);
    try {
      const token = getClientToken();
      const response = await fetch(`${API_BASE}/client_notifications.php`, {
        headers: { "X-KG-Client-Authorization": `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (handleClientAuthFailure(response, data)) return;
      if (!response.ok || data.status !== "ok") throw new Error();
      setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
    } catch (_) {
      message.error("Unable to load notifications.");
    } finally {
      setNotificationsLoading(false);
    }
  };

  const markNotificationRead = async (item) => {
    const token = getClientToken();
    if (!token) return;
    try {
      await fetch(`${API_BASE}/client_notification_read.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-KG-Client-Authorization": `Bearer ${token}` },
        body: JSON.stringify({ broadcast_id: item.id }),
      });
      setNotifications((items) => items.map((n) => n.id === item.id ? { ...n, read_at: n.read_at || new Date().toISOString() } : n));
      if (item.deep_link && String(item.deep_link).startsWith("/client/")) navigate(item.deep_link);
    } catch (_) {}
  };

  const logout = async () => {
    const token = getClientToken();

    try {
      if (token) {
        await unregisterMyWealthPushFromServer();
        await fetch(`${API_BASE}/client_logout.php`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-KG-Client-Authorization": `Bearer ${token}`,
          },
        });
      }
    } catch (error) {
      // Local logout must still complete if the network is unavailable.
    } finally {
      clearClientSession();
      window.location.replace("/client/login");
    }
  };

  const menuItems = [
    {
      key: "dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
      onClick: () => {
        // Dashboard may already be the active React route while a report drill-down
        // is open. Navigating to the same URL does not remount it, so explicitly
        // tell the dashboard to return to its root view first.
        window.dispatchEvent(new CustomEvent("kg-client-dashboard-home"));
        setDrawerOpen(false);
        navigate("/client/dashboard", { replace: true });
      },
    },
    {
      key: "notifications",
      icon: <BellOutlined />,
      label: "Notifications",
      onClick: openNotifications,
    },
    {
      key: "privacy-security",
      icon: <SafetyOutlined />,
      label: "Privacy & Security",
      children: [
        { key: "change-password", icon: <LockOutlined />, label: "Change Password", onClick: openChangePassword },
        { key: "change-mpin", icon: <SafetyOutlined />, label: "Change MPIN", onClick: openChangeMpin },
        ...(biometricAvailable ? [{
          key: "biometric-login",
          icon: <SafetyOutlined />,
          label: biometricBusy ? "Biometric Login..." : `Biometric Login: ${biometricEnabled ? "ON" : "OFF"}`,
          onClick: toggleBiometric,
        }] : []),
      ],
    },
    {
      key: "portfolio",
      icon: <PieChartOutlined />,
      label: "Portfolio (Coming Soon)",
      disabled: true,
    },
    {
      key: "reports",
      icon: <FileTextOutlined />,
      label: "Reports",
      children: [
        {
          key: "capital-gains-report",
          label: "Capital Gains Report",
          onClick: () => { setDrawerOpen(false); navigate("/client/capital-gains-report"); },
        },
        {
          key: "my-sips",
          label: "My SIPs",
          onClick: () => { setDrawerOpen(false); navigate("/client/my-sips"); },
        },
        {
          key: "my-stps",
          label: "My STPs",
          onClick: () => { setDrawerOpen(false); navigate("/client/my-stps"); },
        },
      ],
    },
    {
      key: "my-downloads",
      icon: <DownloadOutlined />,
      label: "My Downloads",
      onClick: () => { setDrawerOpen(false); navigate("/client/my-downloads"); },
    },
  ];

  const SidebarHeader = (
    <div style={{ padding: "10px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", gap: 8 }}>
      {isDesktop && (
        <Tooltip title={collapsed ? "Expand" : "Collapse"}>
          <Button type="text" size="small" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />} onClick={() => setCollapsed((v) => !v)} />
        </Tooltip>
      )}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flex: 1, overflow: "hidden" }}>
        <UserOutlined style={{ marginTop: 3 }} />
        {!collapsed && (
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, lineHeight: 1.25 }}>
              {clientName}
            </div>
            <div style={{ fontSize: 11, color: "#8c8c8c", marginTop: 3, whiteSpace: "nowrap" }}>
              PAN: {dashboardPan || "Verifying..."}
            </div>
          </div>
        )}
      </div>
      {!collapsed && (
        <Tooltip title="Logout">
          <Button type="text" danger icon={<LogoutOutlined />} onClick={logout} />
        </Tooltip>
      )}
    </div>
  );

  const SidebarMenu = <Menu mode="inline" selectable={false} items={menuItems} />;

  return (
    <>
      <SessionActivityMonitor realm="client" />
      <Layout style={{ minHeight: "100vh" }}>
      {!isDesktop && (
        <Button type="primary" shape="circle" size="large" icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)}
          style={{ position: "fixed", top: 12, left: 12, zIndex: 1000 }} aria-label="Open client menu" />
      )}

      {isDesktop && (
        <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} width={240} theme="light"
          style={{ borderRight: "1px solid #f0f0f0", background: "#fff" }}>
          {SidebarHeader}
          {SidebarMenu}
        </Sider>
      )}

      {!isDesktop && (
        <Drawer title={<span><UserOutlined /> {clientName}{dashboardPan ? <span style={{ display: "block", fontSize: 11, fontWeight: 400, color: "#8c8c8c", marginLeft: 22 }}>Dashboard PAN: {dashboardPan}</span> : null}</span>} placement="left" onClose={() => setDrawerOpen(false)} open={drawerOpen}
          bodyStyle={{ padding: 0 }} extra={<Button type="text" danger icon={<LogoutOutlined />} onClick={logout} />}>
          {SidebarMenu}
        </Drawer>
      )}

      <Layout>
        <Content style={{ background: "#f5f7fa", minHeight: "100vh", padding: isDesktop ? 28 : "72px 10px 20px", minWidth: 0, maxWidth: "100%", overflowX: "hidden" }}>
          <Outlet />
        </Content>
      </Layout>

      <Modal
        open={notificationsOpen}
        centered
        width={620}
        title="Notifications"
        footer={null}
        onCancel={() => setNotificationsOpen(false)}
      >
        {notificationsLoading ? (
          <div style={{ padding: 20, textAlign: "center" }}>Loading...</div>
        ) : notifications.length === 0 ? (
          <Alert type="info" showIcon message="No notifications yet." />
        ) : (
          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            {notifications.map((item) => (
              <div key={item.id} onClick={() => markNotificationRead(item)} style={{ padding: "12px 4px", borderBottom: "1px solid #f0f0f0", cursor: "pointer", opacity: item.read_at ? 0.7 : 1 }}>
                <div style={{ fontWeight: item.read_at ? 500 : 700 }}>{item.title}</div>
                <div style={{ marginTop: 4 }}>{item.message}</div>
                <div style={{ marginTop: 5, fontSize: 11, color: "#8c8c8c" }}>{item.sent_at || ""}</div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        open={changeMpinOpen}
        centered
        className="mf-forgot-mpin-modal"
        title="Privacy & Security · Change MPIN"
        footer={null}
        closable={!changeMpinLoading}
        maskClosable={false}
        onCancel={closeChangeMpin}
        destroyOnHidden
      >
        {changeMpinSuccess ? (
          <Result status="success" title="MPIN Changed Successfully" subTitle="Your new 4 digit MPIN is active on this device." extra={[<Button type="primary" key="ok" onClick={closeChangeMpin}>OK</Button>]} />
        ) : (
          <div className="kg-change-mpin">
            <div className="mf-forgot-mpin-hero"><img src={mfsetuLogo} alt="MFSetu MyWealth" /><div className="mf-mpin-lock"><SafetyOutlined /></div><div style={{fontWeight:700,fontSize:20,marginTop:8}}>Change Your 4 Digit MPIN</div><div style={{color:"#6b7280",marginTop:4}}>Verify your current MPIN, then choose a different new MPIN.</div></div>
            {changeMpinError && <Alert type="error" showIcon message={changeMpinError} style={{marginBottom:14}} />}
            <div className="mf-mpin-field-label">Current MPIN</div>
            <MpinDigits value={currentMpin} onChange={v=>{setCurrentMpin(v);setChangeMpinError("")}} disabled={changeMpinLoading} ariaLabel="Current MPIN" groupId="current-change-mpin" autoFocus onComplete={()=>document.getElementById("new-change-mpin-0")?.focus()} />
            <div className="mf-mpin-field-label">New MPIN</div>
            <MpinDigits value={newMpin} onChange={v=>{setNewMpin(v);setChangeMpinError("")}} disabled={changeMpinLoading} ariaLabel="New MPIN" groupId="new-change-mpin" onComplete={()=>document.getElementById("confirm-change-mpin-0")?.focus()} />
            <div className="mf-mpin-field-label">Confirm New MPIN</div>
            <MpinDigits value={confirmMpin} onChange={v=>{setConfirmMpin(v);if(v.length===4&&newMpin.length===4&&v!==newMpin)setChangeMpinError("New MPIN and Confirm MPIN do not match.");else setChangeMpinError("")}} disabled={changeMpinLoading} ariaLabel="Confirm New MPIN" groupId="confirm-change-mpin" />
            <Button className="mf-mpin-primary" type="primary" size="large" block loading={changeMpinLoading} disabled={currentMpin.length!==4||newMpin.length!==4||confirmMpin.length!==4} onClick={submitChangeMpin}>Change MPIN</Button>
          </div>
        )}
      </Modal>

      <Modal
        open={changePasswordOpen}
        centered
        title="Change Password"
        okText="Change Password"
        cancelText="Cancel"
        confirmLoading={changePasswordLoading}
        closable={!changePasswordLoading}
        maskClosable={false}
        keyboard={!changePasswordLoading}
        onCancel={closeChangePassword}
        onOk={() => changePasswordForm.submit()}
        footer={changePasswordSuccess ? null : undefined}
        destroyOnHidden
      >
        {changePasswordSuccess ? (
          <Result
            status="success"
            title="Password Changed Successfully"
            subTitle="Your password has been changed successfully."
            extra={[
              <Button
                type="primary"
                key="ok"
                size="large"
                onClick={closeChangePassword}
              >
                OK
              </Button>,
            ]}
          />
        ) : (
          <>
        {changePasswordError && (
          <Alert
            type="error"
            showIcon
            message={changePasswordError}
            style={{ marginBottom: 16 }}
          />
        )}

        <Form
          form={changePasswordForm}
          layout="vertical"
          autoComplete="off"
          onFinish={submitChangePassword}
        >
          <Form.Item
            label="Current Password"
            name="current_password"
            rules={[
              {
                required: true,
                message: "Please enter your Current Password.",
              },
            ]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="Enter Current Password"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item
            label="New Password"
            name="new_password"
            dependencies={["current_password"]}
            rules={[
              {
                required: true,
                message: "Please enter your New Password.",
              },
              {
                min: 8,
                message: "Password must be at least 8 characters.",
              },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  if (!/[A-Z]/.test(value)) {
                    return Promise.reject(
                      new Error("Password must contain one uppercase letter.")
                    );
                  }
                  if (!/[a-z]/.test(value)) {
                    return Promise.reject(
                      new Error("Password must contain one lowercase letter.")
                    );
                  }
                  if (!/[0-9]/.test(value)) {
                    return Promise.reject(
                      new Error("Password must contain one number.")
                    );
                  }
                  if (!/[^A-Za-z0-9]/.test(value)) {
                    return Promise.reject(
                      new Error("Password must contain one special character.")
                    );
                  }
                  if (
                    value === changePasswordForm.getFieldValue("current_password")
                  ) {
                    return Promise.reject(
                      new Error(
                        "New Password must be different from Current Password."
                      )
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="Enter New Password"
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            label="Confirm New Password"
            name="confirm_password"
            dependencies={["new_password"]}
            rules={[
              {
                required: true,
                message: "Please confirm your New Password.",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("new_password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      "New Password and Confirm Password do not match."
                    )
                  );
                },
              }),
            ]}
          >
            <Input.Password
              size="large"
              prefix={<LockOutlined />}
              placeholder="Confirm New Password"
              autoComplete="new-password"
              onPressEnter={() => changePasswordForm.submit()}
            />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            message="Password must have at least 8 characters with uppercase, lowercase, number and special character."
          />
        </Form>
          </>
        )}
      </Modal>
      </Layout>
    </>
  );
}

export default AppLayoutForClient;
